/**
 * 直付通科技独立收银台 —— 与模板 zhifutdPay.html 对齐。
 * PC 进页出链，二维码画上游返回的支付链接。手机点「立即支付」再出链，打开同一条返回链接。
 */
import { executeZhifutdPay, fetchZhifutdCashierPage } from '@/api/api'
import { buildQrImageSrc, computeQrPixelSize } from '@/utils/qrRender'

/** 只认 UA。alipayjsapi 会在 Chrome 里挂 window.ap，不能用来判断是否在支付宝内。 */
export function isAlipayWebView () {
  if (typeof navigator === 'undefined') return false
  const ua = (navigator.userAgent || '').toLowerCase()
  return ua.indexOf('alipayclient') >= 0 || ua.indexOf('aliapp') >= 0
    || ua.indexOf('nebula') >= 0 || ua.indexOf('alipay') >= 0
}

export function isDesktopBrowser () {
  if (typeof navigator === 'undefined') return false
  if (isAlipayWebView()) return false
  const ua = (navigator.userAgent || '').toLowerCase()
  return !/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(ua)
}

export function isExecutePayloadReady (d) {
  if (!d || d.expired) return false
  return !!(String(d.payUrl || '').trim() || String(d.codeUrl || '').trim() || String(d.channelPayUrl || '').trim())
}

export async function loadPageContext (orderId, token) {
  const ctx = await fetchZhifutdCashierPage(orderId, token)
  if (!ctx) {
    throw new Error('页面加载失败')
  }
  return ctx
}

export async function requestExecute (orderId, token) {
  try {
    const d = await executeZhifutdPay(orderId, token)
    if (!d) throw new Error('网络异常')
    return d
  } catch (err) {
    if (err && err.data) {
      const vo = err.data
      if (vo.expired) {
        const expiredErr = new Error(vo.message || '订单已过期')
        expiredErr.expiredPayload = vo
        throw expiredErr
      }
      throw new Error(vo.message || err.message || '出链失败')
    }
    throw new Error((err && err.message) || '出链失败')
  }
}

export function formatCountdown (leftMs) {
  const s = Math.max(0, Math.floor(leftMs / 1000))
  const h = String(Math.floor(s / 3600)).padStart(2, '0')
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
  const sec = String(s % 60).padStart(2, '0')
  return h + ':' + m + ':' + sec
}

export async function renderQrToElement (el, text) {
  if (!el || !text) return false
  const size = computeQrPixelSize(text)
  const src = await buildQrImageSrc(text, { pixelSize: size })
  if (!src) return false
  el.innerHTML = ''
  const img = document.createElement('img')
  img.src = src
  img.width = size
  img.height = size
  img.alt = '支付二维码'
  el.appendChild(img)
  return true
}

function isHttpPayUrl (raw) {
  return /^https?:\/\//i.test(String(raw || '').trim())
}

function isAlipayScheme (raw) {
  return /^alipays?:\/\//i.test(String(raw || '').trim())
}

/** 上游原文。官方码优先，否则 payUrl / channelPayUrl / codeUrl。 */
export function pickReturnedPayUrl (payload) {
  if (!payload) return ''
  const codeUrl = String(payload.codeUrl || '').trim()
  if (/qr\.alipay\.com/i.test(codeUrl) || /^alipayqr:\/\//i.test(codeUrl)) return codeUrl
  const pay = String(payload.payUrl || payload.channelPayUrl || '').trim()
  if (isHttpPayUrl(pay) || isAlipayScheme(pay)) return pay
  if (isHttpPayUrl(codeUrl) || isAlipayScheme(codeUrl)) return codeUrl
  const channel = String(payload.channelPayUrl || '').trim()
  if (isHttpPayUrl(channel) || isAlipayScheme(channel)) return channel
  return ''
}

/** 二维码内容就是上游返回的支付链接。 */
export function pickQrText (payload) {
  return pickReturnedPayUrl(payload)
}

function assignAlipayScheme (scheme) {
  const u = String(scheme || '').trim()
  if (!u) return
  try {
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.src = u
    document.body.appendChild(iframe)
  } catch (e) { /* ignore */ }
  setTimeout(() => { window.location.href = u }, 120)
}

function openDirectPay (payload) {
  const payUrl = pickReturnedPayUrl(payload)
  if (!payUrl) {
    throw new Error('支付链接缺失')
  }
  if (isAlipayScheme(payUrl)) {
    assignAlipayScheme(payUrl)
    return
  }
  if (!isHttpPayUrl(payUrl)) {
    throw new Error('支付链接缺失')
  }
  if (isAlipayWebView()) {
    window.location.href = payUrl
    return
  }
  assignAlipayScheme('alipays://platformapi/startapp?appId=20000067&url=' + encodeURIComponent(payUrl))
}

/** 打开上游返回的支付链接。支付宝内直接进入；支付宝外用 20000067 拉起。 */
export async function tryZhifutdHalfScreen (payload) {
  if (!isExecutePayloadReady(payload)) return false
  openDirectPay(payload)
  return true
}
